import { css, StyleSheet } from 'aphrodite'
import React, { useContext, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import Geocode from 'react-geocode'
import {
  COLOR_BLACK,
  COLOR_ERROR,
  COLOR_GRAY__300,
  COLOR_GRAY__600,
  COLOR_WHITE,
} from '../../lib/colors'
import { useMutation, useQuery } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'
import { toast } from 'react-toastify'
import api from '../../api'
import LiveContext from '../../components/Context/LiveContext'
import UIAutocompleteCompanies from '../../components/UI/UIAutoCompleteCompanies'
import UIButton from '../../components/UI/UIButton'
import UICheckbox from '../../components/UI/UICheckbox'
import UIInput from '../../components/UI/UIInput'
import CREATE_COMPANY from '../../graphql/Companies/createCompany'
import GET_WATCHER from '../../graphql/Watcher/getWatcher'
import { emailRegExp, siretRegExp } from '../../lib/helpers'
import { leToBr } from '../../utils/Utils'
import GooglePlacesAutocomplete from 'react-google-places-autocomplete'
import { X } from 'react-feather'
const styles = StyleSheet.create({
  container: {
    padding: '0 16px 16px',
    height: '100%',
  },
  myAccountContainer: {
    backgroundColor: COLOR_WHITE,
    borderRadius: '10px',
    padding: 30,
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    width: '100%',
  },
  myAccountTitle: {
    fontWeight: '600',
    fontSize: '28px',
    margin: 0,
    '@media (max-width: 480px)': {
      marginTop: 20,
    },
  },
  personnalInformationTitle: {
    fontWeight: '700',
    fontSize: '18px',
  },
  personnalInformationContainer: {
    width: '50%',
    '@media (max-width: 480px)': {
      width: '100%',
    },
  },
  countrySection: {
    display: 'flex',
    flexDirection: 'row',
  },
  chackboxContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    alignItems: 'center',
  },
  buttonStyle: {
    width: '40%',
    '@media (max-width: 480px)': {
      width: '75%',
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '100px',
  },
  loading: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  enterpriseInfo: {
    marginTop: 50,
  },
  addressInput: {
    position: 'relative',
  },
  resetAddress: {
    position: 'absolute',
    background: 'transparent',
    border: 'none',
    right: 0,
    top: 0,
    bottom: 0,
    cursor: 'pointer',
  },
  error: {
    fontWeight: 400,
    fontSize: 11,
    color: COLOR_ERROR,
  },
})

const customStyles = (error, isEmpty) => {
  return {
    control: (provided) => ({
      ...provided,
      borderColor: error ? COLOR_ERROR : COLOR_GRAY__300,
      fontSize: 15,
    }),
    valueContainer: (provided) => ({
      ...provided,
      display: 'flex',
      padding: '10px 16px',
      alignItems: 'center',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      display: 'none',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isEmpty ? COLOR_GRAY__600 : COLOR_BLACK,
    }),
  }
}

const MyAccount = () => {
  const { t } = useTranslation('translation')
  const [isEnterPrise, setIsEnterPrise] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [newsletterChecked, setNewsletterChecked] = useState(false)
  const [companySelected, setCompanySelected] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [companyEmptyError, setCompanyEmptyError] = useState(false)
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [country, setCountry] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [addressEmptyError, setAddressEmptyError] = useState(false)
  const [fullAddress, setFullAddress] = useState('')

  const { idWatcher } = useContext(LiveContext)

  const onCompanySelect = (value, selected) => {
    setCompanySelected(selected?.id)
    setValue('siret', selected?.siret)
    setValue('tva', selected?.tvaNumber)
  }

  const onCompanyChange = (company) => {
    if (company.length === 0) {
      setCompanySelected('')
    } else {
      setCompanyName(company)
    }
  }

  const onCompanyBlur = (e) => {
    e.preventDefault()
    companyName === ''
      ? setCompanyEmptyError(true)
      : setCompanyEmptyError(false)
  }

  const schema = yup.object().shape({
    lastName: yup
      .string()
      .required(t('common.form.errorMessage.lastNameEmptyError')),
    nickName: yup
      .string()
      .required(t('common.form.errorMessage.nickNameEmptyError')),
    firstName: yup
      .string()
      .required(t('common.form.errorMessage.firstNameEmptyError')),
    email: yup
      .string()
      .matches(
        emailRegExp,
        t('common.form.errorMessage.invalidEmailEmptyError')
      )
      .required(t('common.form.errorMessage.emailEmptyError')),
    phoneNumber: yup
      .string()
      .required(t('common.form.errorMessage.phoneNumberEmptyError')),
    enterPriseName: yup.string(),
    siret: yup
      .string()
      .matches(siretRegExp, t('common.form.errorMessage.siretError'))
      .when('controlEnterprise', {
        is: true,
        then: yup
          .string()
          .required(t('common.form.errorMessage.siretEmptyError')),
        otherwise: yup.string(),
      }),
    tva: yup
      .string()
      .matches(/^([FR])*(\d{11})$/, t('common.form.errorMessage.tvaError'))
      .when('controlEnterprise', {
        is: true,
        then: yup
          .string()
          .required(t('common.form.errorMessage.tvaEmptyError')),
        otherwise: yup.string(),
      }),
  })

  const {
    formState: { errors },
    control,
    handleSubmit,
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  })

  const [createCompany] = useMutation(CREATE_COMPANY)

  const { refetch, loading } = useQuery(GET_WATCHER, {
    variables: { idWatcher },
    onCompleted: (data) => {
      reset({
        lastName: data?.watcher?.lastname,
        nickName: data?.watcher?.nickname,
        firstName: data?.watcher?.firstname,
        email: data?.watcher?.email,
        phoneNumber: data?.watcher?.phoneNumber,
        newsletterChecked: data?.watcher?.consent_newsletter,
        siret: data?.watcher?.companyByIdCompany?.siret,
        tva: data?.watcher?.companyByIdCompany?.tvaNumber,
      })
      setFullAddress(
        data?.watcher?.formattedAddress
          ? data?.watcher?.formattedAddress
          : `${data?.watcher?.addressLine1}, ${data?.watcher?.postcode} ${data?.watcher?.cityName}, ${data?.watcher?.countryName}`
      )
      setAddress(data?.watcher?.addressLine1)
      setZipCode(data?.watcher?.postcode)
      setCity(data?.watcher?.cityName)
      setCountry(data?.watcher?.countryName)
      setCompanyName(data?.watcher?.companyByIdCompany?.name)
      setNewsletterChecked(data?.watcher?.consentNewsletter)
      if (data?.watcher?.companyByIdCompany) setIsEnterPrise(true)
    },
    onError: () => {
      toast.error(t('pages.viewerAccount.demand.fetchError'))
    },
  })

  const updateAccount = (variables) => {
    api
      .put(`/me`, variables)
      .then(() => {
        refetch({
          idWatcher,
        })
        toast.success(t('successUpdateAccount'), {
          position: 'top-center',
        })
        setLoading(false)
      })
      .catch((error) => {
        toast.error(t('errorUpdateAccount'), {
          position: 'top-center',
        })
        setLoading(false)
        console.log('error update user : ', error)
      })
  }

  const onSubmit = (data) => {
    setLoading(true)
    const variables = {
      idWatcher,
      nickname: data.nickName,
      firstname: data.firstName,
      email: data.email,
      lastname: data.lastName,
      phone_number: data.phoneNumber,
      tva: data.tva,
      siret: data.siret,
      formatted_address: fullAddress,
      city_name: city,
      country_name: country,
      postcode: zipCode,
      address_line1: address,
      consent_newsletter: newsletterChecked,
    }
    if (companySelected || companySelected === null) {
      variables.id_company = companySelected
    }
    if (!companySelected && isEnterPrise) {
      createCompany({
        variables: {
          companyName: companyName,
          tvaNumber: data.tva,
          siret: data.siret,
        },
      }).then((data) => {
        if (data?.data?.createCompany?.company?.id) {
          variables.id_company = data?.data?.createCompany?.company?.id
          updateAccount(variables)
        }
      })
    } else {
      updateAccount(variables)
    }
  }

  const parseAddress = async (location) => {
    Geocode.fromAddress(location.value.description).then(
      (response) => {
        setAddress(response.results[0].formatted_address.split(',')[0])
        setCity(
          response.results[0].address_components.find((component) =>
            component.types.includes('locality')
          )?.long_name ||
            response.results[0].address_components.find((component) =>
              component.types.includes('sublocality')
            )?.long_name ||
            ''
        )
        setCountry(
          response.results[0].address_components.find((component) =>
            component.types.includes('country')
          )?.long_name || ''
        )
        setZipCode(
          response.results[0].address_components.find((component) =>
            component.types.includes('postal_code')
          )?.long_name || ''
        )
      },
      (error) => {
        console.log(error)
      }
    )
  }

  return (
    <>
      {loading ? (
        <div className={css(styles.loading)}>
          {t('pages.viewerAccount.demand.loading')}
        </div>
      ) : (
        <div className={css(styles.container)}>
          <div className={css(styles.myAccountContainer)}>
            <p className={css(styles.myAccountTitle)}>{t('myAccount.title')}</p>
            <form
              className={css(styles.myAccountForm)}
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className={css(styles.personnalInformationContainer)}>
                <p className={css(styles.personnalInformationTitle)}>
                  {t('myAccount.personnalInformations')}
                </p>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <UIInput
                      hasMargin={false}
                      {...field}
                      placeholder={`${t('common.form.firstName')}*`}
                      errorMessage={
                        errors.firstName && errors.firstName.message
                      }
                    />
                  )}
                />
                <Controller
                  name="nickName"
                  control={control}
                  render={({ field }) => (
                    <UIInput
                      hasMargin={false}
                      {...field}
                      placeholder={`${t('common.form.nickName')}*`}
                      errorMessage={errors.nickName && errors.nickName.message}
                    />
                  )}
                />
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <UIInput
                      hasMargin={false}
                      {...field}
                      placeholder={`${t('common.form.lastName')}*`}
                      errorMessage={errors.lastName && errors.lastName.message}
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <UIInput
                      {...field}
                      placeholder={`${t('common.form.email')}*`}
                      errorMessage={errors.email && errors.email.message}
                    />
                  )}
                />
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field }) => (
                    <UIInput
                      {...field}
                      placeholder={`${t('common.form.phoneNumber')}*`}
                      errorMessage={
                        errors.phoneNumber && errors.phoneNumber.message
                      }
                    />
                  )}
                />
                <Controller
                  name="newsletter"
                  control={control}
                  render={({ field }) => (
                    <UICheckbox
                      dynamicStyle={styles.check}
                      checked={newsletterChecked}
                      {...field}
                      onChange={(e) => setNewsletterChecked(e.target.checked)}
                    >
                      {leToBr(t('pages.registerWatcher.optinNewsletters'))}
                    </UICheckbox>
                  )}
                />
              </div>
              <div className={css(styles.personnalInformationContainer)}>
                <p className={css(styles.personnalInformationTitle)}>
                  {t('myAccount.myAddress')}
                </p>
                <div className={css(styles.addressInput)}>
                  <GooglePlacesAutocomplete
                    apiOptions={{ language: 'fr', region: 'fr' }}
                    selectProps={{
                      value: fullAddress,
                      onChange: (location) => {
                        setAddressEmptyError(false)
                        setFullAddress(location.label)
                        parseAddress(location)
                      },
                      placeholder: fullAddress
                        ? fullAddress
                        : t('pages.createRequest.enterTheFullAddress'),
                      styles: customStyles(addressEmptyError, !fullAddress),
                    }}
                  />
                  <button
                    className={css(styles.resetAddress)}
                    type="button"
                    onClick={() => setFullAddress('')}
                  >
                    <X color={COLOR_GRAY__600} />
                  </button>
                </div>
                {addressEmptyError && (
                  <p className={css(styles.error)}>
                    {t('common.form.errorMessage.addressEmptyError')}
                  </p>
                )}
                <div className={css(styles.chackboxContainer)}>
                  <UICheckbox
                    checked={isEnterPrise}
                    onChange={(e) => {
                      setIsEnterPrise(e.target.checked)
                      setValue('controlEnterprise', e.target.checked)
                      if (e.target.checked === false) {
                        setCompanySelected(null)
                      }
                    }}
                  />
                  <p>{t('common.form.enterpriseAccount')}</p>
                </div>
                {isEnterPrise ? (
                  <>
                    <Controller
                      name="enterPriseName"
                      control={control}
                      render={() => (
                        <UIAutocompleteCompanies
                          placeholder={`${t('common.form.enterPriseName')}*`}
                          onSelect={onCompanySelect}
                          onChange={onCompanyChange}
                          onBlur={onCompanyBlur}
                          dynamicStyle={styles.input}
                          errorMessage={
                            companyEmptyError &&
                            t(
                              'common.form.errorMessage.enterpriseNameEmptyError'
                            )
                          }
                          companyValue={companyName}
                        />
                      )}
                    />

                    <Controller
                      name="siret"
                      control={control}
                      render={({ field }) => (
                        <UIInput
                          {...field}
                          placeholder={`${t('pages.registerWatcher.siret')}*`}
                          errorMessage={errors.siret && errors.siret.message}
                        />
                      )}
                    />
                    <Controller
                      name="tva"
                      control={control}
                      render={({ field }) => (
                        <UIInput
                          {...field}
                          placeholder={`${t('pages.registerWatcher.tva')}*`}
                          errorMessage={errors.tva && errors.tva.message}
                        />
                      )}
                    />
                  </>
                ) : null}
              </div>

              <div className={css(styles.buttonContainer)}>
                <UIButton
                  type="submit"
                  kind="button"
                  design="primary"
                  disabled={isLoading || !fullAddress}
                  label={t('common.form.saveModification')}
                  dynamicStyle={styles.buttonStyle}
                  fullWidth
                />
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

export default MyAccount
